import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

export type LayoutType = 'span1' | 'span2' | 'span3' | 'span4' | 'span5' | 'span6' | 'span7' | 'span8' | 'span9';

export type DashboardType = 'dashboard' | 'controls' | 'widgets';

export interface Dashboard {
  id: string;
  brand_id: string;
  name: string;
  description: string | null;
  type?: DashboardType;
  user_id?: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface DashboardItem {
  id: string;
  dashboard_id: string;
  component_key: string;
  label: string;
  category: string;
  route: string;
  layout: LayoutType;
  order: number;
  width: number | null;
  height: number | null;
  x: number | null;
  y: number | null;
  created_at: string;
}

export function useDashboards(brandId: string | undefined, typeFilter?: DashboardType) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: dashboards, isLoading } = useQuery({
    queryKey: ['dashboards', brandId, typeFilter, user?.id],
    queryFn: async () => {
      if (!brandId) return [];
      let query = supabase
        .from('os_experiments_dashboards')
        .select('*')
        .eq('brand_id', brandId)
        .or(user?.id ? `user_id.is.null,user_id.eq.${user.id}` : 'user_id.is.null')
        .order('updated_at', { ascending: false });

      if (typeFilter) {
        query = query.eq('type', typeFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Dashboard[];
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000, // 5 min - cache like dashboard
  });

  const createDashboardMutation = useMutation({
    mutationFn: async (dashboard: Partial<Dashboard>) => {
      if (!brandId) throw new Error('No brand selected');
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id ?? null;

      const type = dashboard.type ?? 'dashboard';
      const isPersonalOverlay = type === 'controls' || type === 'widgets';

      const { data, error } = await supabase
        .from('os_experiments_dashboards')
        .insert({
          brand_id: brandId,
          name: dashboard.name || 'Untitled Dashboard',
          description: dashboard.description || null,
          type,
          user_id: dashboard.user_id ?? (isPersonalOverlay ? userId : null),
          created_by: userId,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Dashboard;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards', brandId] });
      toast.success('Dashboard created');
    },
    onError: (error) => {
      toast.error('Failed to create dashboard: ' + error.message);
    },
  });

  const updateDashboardMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Dashboard> & { id: string }) => {
      const { data, error } = await supabase
        .from('os_experiments_dashboards')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Dashboard;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards', brandId] });
      toast.success('Dashboard saved');
    },
    onError: (error) => {
      toast.error('Failed to save dashboard: ' + error.message);
    },
  });

  const deleteDashboardMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_experiments_dashboards')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards', brandId] });
      toast.success('Dashboard deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete dashboard: ' + error.message);
    },
  });

  return {
    dashboards,
    isLoading,
    createDashboard: createDashboardMutation.mutateAsync,
    updateDashboard: updateDashboardMutation.mutateAsync,
    deleteDashboard: deleteDashboardMutation.mutateAsync,
  };
}

export interface CanvasItemInput {
  id: string;
  componentKey: string;
  label: string;
  category: string;
  route: string;
  layout: LayoutType;
  order: number;
  width?: number;
  height?: number;
  x?: number;
  y?: number;
}

export function useDashboardItems(dashboardId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: items, isLoading } = useQuery({
    queryKey: ['dashboard-items', dashboardId],
    queryFn: async () => {
      if (!dashboardId) return [];
      const { data, error } = await supabase
        .from('os_experiments_dashboard_items')
        .select('*')
        .eq('dashboard_id', dashboardId)
        .order('order', { ascending: true });

      if (error) throw error;
      return data as DashboardItem[];
    },
    enabled: !!dashboardId,
    staleTime: 5 * 60 * 1000, // 5 min - cache like dashboard
  });

  const saveItemsMutation = useMutation({
    mutationFn: async (canvasItems: CanvasItemInput[]) => {
      if (!dashboardId) throw new Error('No dashboard selected');

      await supabase
        .from('os_experiments_dashboard_items')
        .delete()
        .eq('dashboard_id', dashboardId);

      if (canvasItems.length > 0) {
        const { error } = await supabase.from('os_experiments_dashboard_items').insert(
          canvasItems.map((item) => ({
            dashboard_id: dashboardId,
            component_key: item.componentKey,
            label: item.label,
            category: item.category,
            route: item.route,
            layout: item.layout,
            order: item.order,
            width: item.width ?? null,
            height: item.height ?? null,
            x: item.x ?? null,
            y: item.y ?? null,
          }))
        );

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-items', dashboardId] });
      toast.success('Dashboard saved');
    },
    onError: (error) => {
      toast.error('Failed to save dashboard: ' + error.message);
    },
  });

  return {
    items: items ?? [],
    isLoading,
    saveItems: saveItemsMutation.mutateAsync,
  };
}
